import _ from "lodash";
import React from "react";
import classes from "./MultiSelectComponentV2.scss";
import PropTypes from "prop-types";
import EmptyField from "UIComponents/EmptyField";
import LinkWithTooltip from "UIComponents/LinkWithTooltip";
import { InfoIcon } from "SvgComponents";
import update from "immutability-helper";
import UIBaseComponent from "UIComponents/UIBaseComponent";
import classNames from "classnames";
import { colors } from "Constants";
import { CardSelection } from "@toddle-edu/ds-web";
import ConditionalWrapper from "Utils/ConditionalWrapper";

class MultiSelectComponentV2 extends UIBaseComponent {
  isValidValue = value => {
    return !!value && value.length > 0;
  };

  onClickItem = params => {
    const {
      isGroupedOptions,
      options,
      minimumSelectedOptionsCount,
      onMinimumSelectedOptionsRemove,
      displayDisabledAsActive,
      disabled,
    } = this.props;
    let selectedFocusAreas = this.props.value ? this.props.value : [];
    if (disabled && displayDisabledAsActive) return;

    if (this.props.multi) {
      switch (params["operation"]) {
        case "add":
          selectedFocusAreas = update(selectedFocusAreas, {
            $push: [params.value],
          });
          break;
        case "remove": {
          if (minimumSelectedOptionsCount) {
            if (_.size(selectedFocusAreas) == minimumSelectedOptionsCount) {
              onMinimumSelectedOptionsRemove &&
                onMinimumSelectedOptionsRemove();
              break;
            }
          }
          const index = _.indexOf(this.props.value, params["value"]);
          selectedFocusAreas = update(selectedFocusAreas, {
            $splice: [[index, 1]],
          });
          break;
        }
      }
    } else {
      switch (params["operation"]) {
        case "add":
          selectedFocusAreas = [params.value];
          break;
        case "remove":
          selectedFocusAreas = [];
          break;
      }
    }

    if (selectedFocusAreas != this.props.value) {
      this.updateValue(selectedFocusAreas, {
        nodes: !isGroupedOptions
          ? options
          : _.reduce(
              options,
              (result, opt) => {
                result = _.concat(result, opt.children);
                return result;
              },
              []
            ),
      });
    }
  };

  UNSAFE_componentWillReceiveProps = nextProps => {
    if (_.xorBy(this.props.options, nextProps.options, "value").length > 0) {
      const updatedValue = _.filter(
        nextProps.value,
        val => _.findIndex(nextProps.options, { value: val }) != -1
      );

      this.updateValue(updatedValue, { nodes: this.props.options });
    }
  };

  updateNodesRef = ({ ref, id }) => {
    const { updateNodesRef } = this.props;
    if (updateNodesRef) {
      updateNodesRef({ ref, id });
    }
  };

  getSelectList = () => {
    const {
      isGroupedOptions,
      options,
      listContainerStyle,
      groupedLabelStyle,
      groupedOptionsContainerStyle,
      groupedHeaderStyle,
      fieldTypeConfig,
      isMultiSelectComponentV2,
      dsVersion,
    } = this.props;

    const useListStyle = _.get(fieldTypeConfig, "useListStyle", "flex");
    const isDSV2 = dsVersion === "2.0";

    const listContainer = classNames(
      { [classes.fieldCompFlex]: useListStyle === "flex" },
      {
        [classes.fieldCompGrid]:
          useListStyle === "grid" || isMultiSelectComponentV2,
      },
      {
        [classes.fieldCompGridV2]:
          (useListStyle === "grid" || isMultiSelectComponentV2) && isDSV2,
      },
      { [classes.listContainerV2]: isMultiSelectComponentV2 }
    );

    const groupedOptionsContainer = classNames(
      { [classes.groupedOptionsContainer]: true },
      { [classes.groupedOptionsContainerV2]: isMultiSelectComponentV2 }
    );

    const groupedHeader = classNames(
      { [classes.groupedHeader]: true },
      { [classes.groupedHeaderV2]: isMultiSelectComponentV2 }
    );

    if (!isGroupedOptions) {
      //Iterate over flat options
      return this.getFlatOptionList({ options });
    } else {
      //Iterate over groups (Lens)
      return (
        <div
          className={groupedOptionsContainer}
          style={groupedOptionsContainerStyle}
        >
          {_.map(options, ({ children, id, label, showInfo }) => {
            return (
              <div
                key={id}
                className={classes.groupedOption}
                ref={ref => this.updateNodesRef({ ref, id })}
              >
                <div className={groupedHeader} style={groupedHeaderStyle}>
                  <div
                    className={classes.groupedLabel}
                    style={groupedLabelStyle}
                  >
                    {label}
                  </div>
                </div>

                <div className={listContainer} style={listContainerStyle}>
                  {this.getFlatOptionList({ options: children })}
                </div>
              </div>
            );
          })}
        </div>
      );
    }
  };

  getSelectedValues = ({ options }) => {
    let selectedValues = [];
    if (!_.isEmpty(options)) {
      selectedValues = _.map(options, (itemObject, index) => {
        const isSelected = _.includes(
          this.props.value,
          itemObject.value ? itemObject.value : itemObject.id
        );
        if (isSelected) {
          return itemObject.value ? itemObject.value : itemObject.id;
        } else {
          return;
        }
      });
    }
    return selectedValues;
  };

  getFlatOptionList = ({ options }) => {
    const {
      disabled,
      showSubtextUnderInfo,
      displayDisabledAsActive,
      testId,
      dsVersion,
    } = this.props;
    /*******************************************************************************************************
     * displayDisabledAsActive flag is used to control the visual state of this component,
     * where if it is true, we will show the element in visually active form but
     * functionality wise it will behave like disabled.
     ********************************************************************************************************/
    const showDisabledAsActive = disabled && displayDisabledAsActive;
    if (!_.isEmpty(options)) {
      const selectedValues = this.getSelectedValues({ options });
      return _.map(options, (itemObject, index) => {
        const itemStyles = {
          ...this.props.listItemStyle,
          width: this.props.listItemStyle.width
            ? this.props.listItemStyle.width
            : "100%",
        };

        const itemValue = itemObject.value;
        const isSelected = _.includes(
          this.props.value,
          itemValue ? itemValue : itemObject.id
        );
        const params = {
          value: itemValue ? itemValue : itemObject.id,
          operation: isSelected ? "remove" : "add",
        };

        const suffixIcon =
          itemObject.subText && showSubtextUnderInfo ? (
            <LinkWithTooltip
              tooltip={itemObject.subText}
              placement={"bottom"}
              trigger={["hover"]}
              tooltipContainerStyle={{ width: 284 }}
            >
              <div className={classes.infoIcon}>
                <InfoIcon fill={colors.neutral400} width={14} height={14} />
              </div>
            </LinkWithTooltip>
          ) : null;

        return (
          <ConditionalWrapper
            condition={showDisabledAsActive}
            wrapper={<div className={classes.displayDisabledAsActive} />}
            type={"wrapperAsElement"}
          >
            {/*******************************************************************************************************
             * We have implemented conditionalWrapper to form a parent child relationship between the DS component
             * and this component, to include styling for displayDisabledAsActive state, so please check the css before
             * adding any wrapper element to the component and check that existing behaviour remains the same
             ********************************************************************************************************/}
            <CardSelection
              title={itemObject.label}
              type="checkbox"
              key={itemValue ? itemValue : itemObject.id}
              value={itemValue ? itemValue : itemObject.id}
              containerStyle={itemStyles}
              suffixIcon={suffixIcon}
              disabled={disabled && !displayDisabledAsActive}
              subText={!showSubtextUnderInfo ? itemObject.subText : null}
              selectedValues={selectedValues}
              onCardSelectionClick={() => this.onClickItem(params)}
              testId={`${testId}-${itemValue || itemObject?.id}`}
              dsVersion={dsVersion}
            />
          </ConditionalWrapper>
        );
      });
    } else {
      return <EmptyField title={this.props.emptyText} />;
    }
  };

  renderEdit = () => {
    const focusAreaListComponent = this.getSelectList(this.props.options);
    const {
      listContainerStyle,
      fieldTypeConfig,
      isMultiSelectComponentV2,
      isGroupedOptions,
      dsVersion,
    } = this.props;
    const useListStyle = _.get(fieldTypeConfig, "useListStyle", "flex");
    const isDSV2 = dsVersion === "2.0";

    const listContainer = classNames(
      { [classes.fieldCompFlex]: useListStyle === "flex" },
      {
        [classes.fieldCompGrid]:
          useListStyle === "grid" || isMultiSelectComponentV2,
      },
      {
        [classes.fieldCompGridV2]:
          (useListStyle === "grid" || isMultiSelectComponentV2) && isDSV2,
      },
      { [classes.listContainerV2]: isMultiSelectComponentV2 }
    );

    return (
      <div className={classes.componentContainer}>
        <div className={listContainer} style={listContainerStyle}>
          {focusAreaListComponent}
        </div>
      </div>
    );
  };
}

/*
If isGroupedOptions is true then options will be look like this:
[
  { id: "1", label: "Group 1", children: [] },
  { id: "1", label: "Group 1", children: [] }
];

If isGroupedOptions is false then options will be look like this;
[
  { value: "1", label: "Group 1" },
  { value: "1", label: "Group 1"}
];
*/

MultiSelectComponentV2.propTypes = {
  ...UIBaseComponent.propTypes,
  options: PropTypes.array,
  listItemStyle: PropTypes.object,
  multi: PropTypes.bool,
  isGroupedOptions: PropTypes.bool, //To handle grouped view (MYP Key Concepts),
  showSubtextUnderInfo: PropTypes.bool, //To show sub text under info icon
  selectedTickStyle: PropTypes.object,
  displayDisabledAsActive: PropTypes.bool,
  dsVersion: PropTypes.string,
};
MultiSelectComponentV2.defaultProps = {
  ...UIBaseComponent.defaultProps,
  options: [],
  multi: true,
  listItemStyle: {},
  showSubtextUnderInfo: false,
  isGroupedOptions: false,
  selectedTickStyle: {},
  displayDisabledAsActive: false,
  dsVersion: "1.0",
};

export default MultiSelectComponentV2;
